import { useEffect, useState } from "react";
import { Link, Redirect } from "wouter";
import {
  Anchor,
  ArrowRight,
  Calendar,
  FileText,
  Hash,
  Loader2,
  Video,
} from "lucide-react";
import { ArbiterShell } from "@/components/ArbiterShell";
import { AssistantIAPanel } from "@/components/AssistantIAPanel";
import { Button } from "@/components/ui/button";
import { PlatformTosOnboardingDialog } from "@/components/PlatformTosOnboardingDialog";
import { useAuth, useRole } from "@/contexts/AuthContext";
import {
  type CaseSummary,
  getApiErrorDetail,
  listMyCases,
} from "@/lib/api";

export default function Dashboard() {
  const { session } = useAuth();
  const role = useRole();
  const [cases, setCases] = useState<CaseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMyCases()
      .then((data) => {
        if (!cancelled) setCases(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(getApiErrorDetail(err) ?? "Echec de chargement des dossiers.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // L'avocat (PARTIE) a son cockpit dédié : il ne doit jamais voir le shell
  // arbitre, même s'il atterrit sur /dashboard (session déjà active = pas de
  // redirect post-login). Défense en profondeur du routing par rôle.
  if (role === "PARTIE") {
    return <Redirect to="/avocat" />;
  }

  return (
    <ArbiterShell
      title="Tableau de bord"
      rightPanel={<AssistantIAPanel />}
    >
      {session && <PlatformTosOnboardingDialog userId={session.user.id} />}

      <div className="flex-1 px-4 sm:px-6 py-6 sm:py-8 max-w-5xl mx-auto w-full">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-h1-arbitre">Mes dossiers</h1>
          <p
            className="text-body-arbitre mt-2"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            Liste des procédures dont vous êtes membre. ADMIN voit
            l'intégralité ; PARTIE / ARBITRE voient uniquement leurs
            dossiers (ACL <code className="gsf-mono-proof text-xs">case_members</code>).
          </p>
        </header>

        {cases === null && !error && (
          <div className="flex items-center justify-center py-16">
            <Loader2
              className="animate-spin"
              size={28}
              style={{ color: "var(--proof-gold)" }}
            />
          </div>
        )}

        {error && (
          <div
            className="rounded-md border p-6 text-sm"
            style={{
              borderColor: "color-mix(in oklch, var(--block-red) 40%, transparent)",
              background: "color-mix(in oklch, var(--block-red) 10%, transparent)",
              color: "rgb(254 202 202)",
            }}
          >
            {error}
          </div>
        )}

        {cases && cases.length === 0 && (
          <div className="gsf-surface p-8 text-center text-sm">
            <FileText
              size={28}
              style={{ color: "var(--proof-gold)" }}
              className="mx-auto mb-3"
            />
            <p style={{ color: "var(--arbitre-slate-300)" }}>
              Aucun dossier ne vous est attribué pour l'instant. Un
              administrateur doit vous ajouter aux{" "}
              <code className="gsf-mono-proof text-xs">case_members</code> du
              dossier concerné.
            </p>
          </div>
        )}

        {cases && cases.length > 0 && (
          <ul className="space-y-3">
            {cases.map((c) => (
              <CaseRow key={c.id} item={c} />
            ))}
          </ul>
        )}
      </div>
    </ArbiterShell>
  );
}

function CaseRow({ item }: { item: CaseSummary }) {
  // Status d'ancrage colore par etat (cf. step-* dans index.css).
  const anchorClass =
    item.anchored_status === "SUCCESS"
      ? "step-termine"
      : item.anchored_status === "PENDING"
      ? "step-instruction"
      : item.anchored_status === "FAILED"
      ? "step-failed"
      : "";

  return (
    <li className="gsf-surface p-4 sm:p-5 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
      <div className="flex-1 min-w-0">
        <div
          className="text-caption-arbitre flex items-center gap-1.5 mb-1.5"
          style={{ color: "var(--proof-gold)" }}
        >
          <Hash size={11} />
          <span className="gsf-mono-proof">{item.numero_gsf}</span>
        </div>
        <div className="text-body-lg-arbitre font-medium truncate">
          {item.objet_litige}
        </div>
        <div
          className="mt-2 flex flex-wrap gap-3 text-xs"
          style={{ color: "var(--arbitre-slate-300)" }}
        >
          <span>
            <FileText size={11} className="inline mr-1" />
            {item.statut}
          </span>
          {item.deadline_instruction && (
            <span>
              <Calendar size={11} className="inline mr-1" />
              {new Date(item.deadline_instruction).toLocaleDateString("fr-FR")}
            </span>
          )}
          <span className={anchorClass}>
            <Anchor size={11} className="inline mr-1" />
            {item.anchored_status}
          </span>
        </div>
      </div>
      <div className="flex flex-row sm:flex-col gap-2 shrink-0">
        <Link href={`/cases/${item.id}`}>
          <Button
            size="sm"
            style={{
              background: "var(--proof-gold)",
              color: "var(--graphite-900)",
            }}
          >
            Détail
            <ArrowRight size={14} className="ml-2" />
          </Button>
        </Link>
        <Link href={`/audiences/${item.id}`}>
          <Button
            size="sm"
            variant="ghost"
            style={{ color: "var(--proof-gold)" }}
          >
            <Video size={14} className="mr-2" />
            Audience
          </Button>
        </Link>
      </div>
    </li>
  );
}
